import { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
// components
import TreatmentPlanType from "./components/treatment-plan-type";
import BasicPlan from "./components/basic-plan";
import BasicPlanFields from "./components/basic-plan-fields";
import AdvancedPlanEditor from "./components/advanced-plan-editor";
import ActionsBar from "../../../actions-bar/ActionsBar";
import ConfirmModal from "components/confirm-modal";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import AdvancedPlanFields from "./components/advanced-plan-fields";
// hooks
import useToggleModal from "hooks/useToggleModal";
import { useNoteCodes } from "../../../../contexts/NoteCodesContext";
// styles
import styles from "./styles.module.scss";
// constants
import messages from "messages";
// utils
import getClasses from "utils/getClasses";

type Props = {
  code: ICDCode & { id: string; [key: string]: any };
  onUpdate: (arg: undefined | TreatmentPlan) => void;
};

enum PLAN_TYPES {
  BASIC = "BASIC",
  ADVANCED = "ADVANCED",
}

const TreatmentPlan = ({ code, onUpdate }: Props) => {
  const { readOnly } = useNoteCodes();
  const [value, setValue] = useState<ICDCode["treatment_plan"]>();

  useEffect(() => {
    setValue(code.treatment_plan);
  }, [code.treatment_plan]);

  const {
    show: showDeleteConfirmModal,
    open: openDeleteConfirmModal,
    close: closeDeleteConfirmModal,
  } = useToggleModal(false);

  const {
    show: showTypeModal,
    open: openTypeModal,
    close: closeTypeModal,
  } = useToggleModal(false);

  const {
    show: showBasicModal,
    open: openBasicModal,
    close: closeBasicModal,
  } = useToggleModal(false);

  const {
    show: showAdvancedModal,
    open: openAdvancedModal,
    close: closeAdvancedModal,
  } = useToggleModal(false);
  return (
    <>
      {!value ? (
        // should not show in the read only view
        readOnly ? null : (
          <Button
            variant={"link"}
            disabled={!code.id}
            onClick={() => {
              openTypeModal();
            }}
            className={styles["treatment-plan__text-button"]}
          >
            <OutlinePlus className={styles["treatment-plan__add-treatment"]} />
            {messages.addTreatmentPlan}
          </Button>
        )
      ) : (
        <>
          <div
            className={getClasses(
              styles["treatment-plan__complete"],
              readOnly && styles["treatment-plan__complete--editable"],
            )}
          >
            <div className={styles["treatment-plan__complete-title-bar"]}>
              <div className={styles["treatment-plan__complete-title"]}>
                {messages.treatmentPlan}
              </div>
              {!readOnly && (
                <ActionsBar
                  onUpdate={() => {
                    if (value.type === PLAN_TYPES.BASIC) {
                      openBasicModal();
                    }
                    if (value.type === PLAN_TYPES.ADVANCED) {
                      openAdvancedModal();
                    }
                  }}
                  onDelete={openDeleteConfirmModal}
                  iconClass={styles["treatment-plan__icon"]}
                />
              )}
            </div>
            <div className={styles["treatment-plan__complete-body"]}>
              {value.type === PLAN_TYPES.BASIC && (
                <BasicPlanFields code={code} value={value} readOnly={true} />
              )}
              {value.type === PLAN_TYPES.ADVANCED && (
                <AdvancedPlanFields
                  code={code}
                  value={value as AdvancedTreatmentPlan}
                  readOnly={true}
                />
              )}
            </div>
          </div>

          <ConfirmModal
            labelTitle={messages.areYouSure}
            children={messages.ifYouDeleteTreatment}
            show={showDeleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            onConfirm={() => {
              closeDeleteConfirmModal();
              onUpdate(undefined);
            }}
          />
        </>
      )}

      {/* modals */}
      {/* edit treatment plan type: PLAN_TYPES.BASIC, PLAN_TYPES.ADVANCED */}
      <TreatmentPlanType
        show={showTypeModal}
        onApply={(nextType) => {
          closeTypeModal();

          if (nextType === PLAN_TYPES.BASIC) {
            openBasicModal();
          }

          if (nextType === PLAN_TYPES.ADVANCED) {
            openAdvancedModal();
          }
        }}
        onCancel={closeTypeModal}
      />

      {/* edit basic treatment plan content */}
      {showBasicModal && (
        <BasicPlan
          code={code}
          show={showBasicModal}
          value={value || { type: PLAN_TYPES.BASIC }}
          onApply={(v) => {
            onUpdate(v);
            closeBasicModal();
          }}
          onCancel={closeBasicModal}
        />
      )}

      {/* edit advanced treatment plan content */}
      {showAdvancedModal && (
        <AdvancedPlanEditor
          code={code}
          show={showAdvancedModal}
          value={
            (value as AdvancedTreatmentPlan) || { type: PLAN_TYPES.ADVANCED }
          }
          onApply={(v) => {
            onUpdate(v);
            closeAdvancedModal();
          }}
          onCancel={closeAdvancedModal}
        />
      )}
    </>
  );
};

export default TreatmentPlan;
